// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { constants } from "@pacific/XDL/constants";
import { avoidBreakInsidePrintElement, color, xScreen } from "@pacific/XDL/Utilities";
import { mergeWithCss } from "@pacific/XDL/Utilities/mergeWithCss";
import React, { useState } from "react";
import { css } from "styled-components";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
}

interface OwnProps {
    Image: React.FC;
    Description: React.FC;
    Price: React.FC;
    Subtotal: React.FC;
    Qty: React.FC;
    Total: React.FC;
    Availability: React.FC;
    AvailabilityLink: React.FC;
    AddToList: React.FC;
    Remove: React.FC;
    LineNotesLink: React.FC;
    LineNotes: React.FC;
    Errors: React.FC;
}

export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    subtotal?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    availability?: InjectableCss;
    availabilityLink?: InjectableCss;
    addToList?: InjectableCss;
    remove?: InjectableCss;
    lineNotesLink?: InjectableCss;
    lineNotes?: InjectableCss;
    topSection?: InjectableCss;
    leftSide?: InjectableCss;
    leftSideInfo?: InjectableCss;
    leftSideActions?: InjectableCss;
    rightSide?: InjectableCss;
    notesFieldSection?: InjectableCss;
    priceSection?: InjectableCss;
    rightSideActions?: InjectableCss;
    errorsSection?: InjectableCss;
}

interface CartLineContainerContextType {
    styles: CartLineContainerStyles;
}

const CartLineContainerContext = React.createContext<CartLineContainerContextType>({
    styles: {},
});

export const useCartLineContainerContext = () => {
    const context = React.useContext(CartLineContainerContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};
const containerClassName = "cartline-container";

const sharedNotesFieldSectionStyles: InjectableCss = {
    css: css`
        display: flex;
        width: 100%;
        max-width: 740px;
        margin-top: 8px;
        gap: 22px;
        &:empty {
            display: none;
        }

        &:before {
            content: " ";
            width: 100%;
            max-width: 88px;
        }
        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
            &:before {
                display: none;
            }
        }
    `,
};

export const cartlineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 20px 0;
            border-bottom: 1px solid ${color("common.border")};
            &.${containerClassName}-mobile {
                display: none;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                &.${containerClassName}-desktop {
                    display: none;
                }
                &.${containerClassName}-mobile {
                    display: flex;
                }
            }
            ${avoidBreakInsidePrintElement}
            @media print {
                display: block;
            }
        `,
    },
    image: {
        css: css`
            width: 88px;
            height: 88px;
            min-width: 88px;
        `,
    },

    topSection: {
        css: css`
            display: flex;
            gap: 30px;
            width: 100%;

            @media print {
                flex-direction: column;
            }
        `,
    },
    leftSide: {
        css: css`
            display: flex;
            width: 100%;
            max-width: 510px;
            gap: 22px;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                max-width: 436px;
            }

            @media print {
                width: 100%;
                max-width: 100%;
            }
        `,
    },
    leftSideInfo: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    leftSideActions: {
        css: css`
            display: flex;
            gap: 52px;
            margin-top: 10px;
            height: 18px;
            &:empty {
                display: none;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                gap: unset;
                flex-direction: column;
                height: auto;
            }
        `,
    },
    rightSide: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            max-width: calc(100% - 510px);
            margin-top: 4px;
            justify-content: space-between;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-top: 20px;
                max-width: unset;
            }
            @media print {
                margin-top: 0;
                max-width: unset;
            }
        `,
    },
    notesFieldSection: {
        ...sharedNotesFieldSectionStyles,
    },
    priceSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-bottom: 20px;
            }
        `,
    },
    rightSideActions: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 10px;
            height: 18px;
            &:empty {
                display: none;
            }
            @media print {
                display: none;
            }
        `,
    },
    availability: {
        css: css`
            display: flex;
            flex: 1;
            align-items: flex-end;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-bottom: 4px;
            }
        `,
    },
    lineNotes: {
        css: css`
            width: 100%;
        `,
    },
    remove: {
        css: css`
            display: flex;
        `,
    },
    price: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
            }
        `,
    },
    subtotal: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
                margin-top: -3px;
            }
        `,
    },
    total: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
            }
        `,
    },
    description: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    errorsSection: {
        ...mergeWithCss(sharedNotesFieldSectionStyles, {
            css: css`
                margin-top: 16px;
            `,
        }),
    },
};

const enum NAMES {
    Image = "Image",
    Description = "Description",
    Price = "Price",
    Subtotal = "Subtotal",
    Qty = "Qty",
    Total = "Total",
    Availability = "Availability",
    AvailabilityLink = "AvailabilityLink",
    AddToList = "AddToList",
    Remove = "Remove",
    LineNotesLink = "LineNotesLink",
    LineNotes = "LineNotes",
    Errors = "Errors",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Description),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Subtotal),
        getChild(childrenArray, NAMES.Qty),
        getChild(childrenArray, NAMES.Total),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AvailabilityLink),
        getChild(childrenArray, NAMES.LineNotesLink),
        getChild(childrenArray, NAMES.LineNotes),
        getChild(childrenArray, NAMES.Remove),
        getChild(childrenArray, NAMES.AddToList),
        getChild(childrenArray, NAMES.Errors),
    ];
};

interface RowProps {
    alignItems?: "flex-end" | "center" | "flex-start";
    justifyContent?: "space-between" | "flex-end";
    gap?: number;
}

const CartLineContainer: React.FC<CartLineContainerProps> & OwnProps = ({ children, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));
    const value = React.useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [
        image,
        description,
        price,
        subtotal,
        qty,
        total,
        availability,
        availabilityLink,
        lineNotesLink,
        lineNotes,
        remove,
        addToList,
        errors,
    ] = React.useMemo(() => getDestructeredChildren(childrenArray), [childrenArray]);
    return (
        <CartLineContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} className={`${containerClassName}-desktop`}>
                <StyledWrapper {...styles.topSection} className="top-section">
                    <StyledWrapper {...styles.leftSide} className="left-side">
                        {image}
                        <StyledWrapper {...styles.leftSideInfo} className="left-side-info">
                            {description}
                            <StyledWrapper {...styles.leftSideActions} className="left-side-actions">
                                {lineNotesLink}
                                {addToList}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSide} className="right-side">
                        <StyledWrapper {...styles.priceSection} className="price-section">
                            {price}
                            {subtotal}
                            {qty}
                            {total}
                        </StyledWrapper>
                        {availability}
                        <StyledWrapper {...styles.rightSideActions} className="right-side-actions">
                            {availabilityLink}
                            {remove}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.notesFieldSection} className="notes-field-section">
                    {lineNotes}
                </StyledWrapper>
                <StyledWrapper {...styles.errorsSection} className="errors-section">
                    {errors}
                </StyledWrapper>
            </StyledWrapper>
            {/* Mobile */}
            <StyledWrapper {...styles.container} className={`${containerClassName}-mobile`}>
                <StyledWrapper {...styles.leftSide} className="left-side">
                    {image}
                    <StyledWrapper {...styles.leftSideInfo} className="left-side-info">
                        {description}
                        <StyledWrapper {...styles.leftSideActions} className="left-side-actions">
                            {availability}
                            {availabilityLink}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSide} className="right-side">
                    <StyledWrapper {...styles.priceSection} className="price-section">
                        {qty}
                        {price}
                        {subtotal}
                        {total}
                    </StyledWrapper>
                    {lineNotesLink}
                    <StyledWrapper {...styles.notesFieldSection} className="notes-field-section">
                        {lineNotes}
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSideActions} className="right-side-actions">
                        {addToList}
                        {remove}
                    </StyledWrapper>
                    <StyledWrapper {...styles.errorsSection} className="errors-section">
                        {errors}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContainerContext.Provider>
    );
};

// Image
CartLineContainer.Image = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.image} className={`${containerClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Image.displayName = NAMES.Image;

// Description
CartLineContainer.Description = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.description} className={`${containerClassName}-description`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Description.displayName = NAMES.Description;

// Price
CartLineContainer.Price = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.price} className={`${containerClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Price.displayName = NAMES.Price;

// Subtotal
CartLineContainer.Subtotal = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.subtotal} className={`${containerClassName}-subtotal`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Subtotal.displayName = NAMES.Subtotal;

// Qty
CartLineContainer.Qty = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.qty} className={`${containerClassName}-qty`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Qty.displayName = NAMES.Qty;

// Total
CartLineContainer.Total = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.total} className={`${containerClassName}-total`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Total.displayName = NAMES.Total;

// Availability
CartLineContainer.Availability = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.availability} className={`${containerClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Availability.displayName = NAMES.Availability;

// AvailabilityLink
CartLineContainer.AvailabilityLink = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.availabilityLink} className={`${containerClassName}-availabilityLink`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.AvailabilityLink.displayName = NAMES.AvailabilityLink;

// AddToList
CartLineContainer.AddToList = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.addToList} className={`${containerClassName}-addToList`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.AddToList.displayName = NAMES.AddToList;

// Remove
CartLineContainer.Remove = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.remove} className={`${containerClassName}-remove`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Remove.displayName = NAMES.Remove;

// LineNotesLink
CartLineContainer.LineNotesLink = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.lineNotesLink} className={`${containerClassName}-lineNotesLink`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.LineNotesLink.displayName = NAMES.LineNotesLink;

// LineNotes
CartLineContainer.LineNotes = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.lineNotes} className={`${containerClassName}-lineNotes`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.LineNotes.displayName = NAMES.LineNotes;

// Errors
CartLineContainer.Errors = ({ children }) => {
    return <StyledWrapper className={`${containerClassName}-errors`}>{children}</StyledWrapper>;
};
CartLineContainer.Errors.displayName = NAMES.Errors;

export default CartLineContainer;
